<script lang="ts" setup>
import { useLoginManager } from '@/login/composables/useLoginManager';
import { useQRCode } from '@vueuse/integrations/useQRCode';
import { computed, onUnmounted, ref, watch } from 'vue';
import { BankIDStatus } from '@sysarb/sysarb-types/services/v1/login';
import { useCssVar, useWindowSize } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useLoginStore } from '@/login/stores/useLoginStore';

const { width } = useWindowSize();

const loginStore = useLoginStore();
const { loginWithBankId, bankIdQrCode, bankIdStatus, bankIdCanRetry, abortBankId, bankIdLink, bankIdInProgress } = useLoginManager();
const { t } = useI18n();

const light = useCssVar('--qr-color-light');
const dark = useCssVar('--qr-color-dark');

const qrCodeDisplay = computed(() => bankIdQrCode.value ?? '');
const qrcode = useQRCode(qrCodeDisplay, {
    margin: 0,
    width: 1024,
    version: 8,
    color: {
        light: light.value ?? undefined,
        dark: dark.value ?? undefined
    }
});

const showQrCode = computed(() => qrCodeDisplay.value !== '' && bankIdStatus.value === BankIDStatus.PendingWait);

const statusMessage = computed(() => {
    switch (bankIdStatus.value) {
        case BankIDStatus.PendingSign:
            return t('components.login.openBankIdAppDescriptionVerify');
        case BankIDStatus.Cancelled:
        case BankIDStatus.CertificateError:
        case BankIDStatus.Error:
        case BankIDStatus.Timeout:
        case BankIDStatus.Expired:
        case BankIDStatus.Failed:
        case BankIDStatus.UserCanceled:
        case BankIDStatus.Complete:
        default:
            return null;
    }
});

const errorMessage = computed(() => {
    switch (bankIdStatus.value) {
        case BankIDStatus.Cancelled:
            return t('bankIdStatus.8');
        case BankIDStatus.CertificateError:
            return t('bankIdStatus.6');
        case BankIDStatus.Error:
            return t('bankIdStatus.4');
        case BankIDStatus.Timeout:
        case BankIDStatus.Expired:
            return t('bankIdStatus.5');
        case BankIDStatus.Failed:
            return t('bankIdStatus.2');
        case BankIDStatus.UserCanceled:
            return t('bankIdStatus.7');
        default:
            return null;
    }
});

const logInOnThisDevice = ref(width.value <= 640);

function startLoginWithBankId(value: boolean): void {
    logInOnThisDevice.value = value;
    void loginWithBankId();
}

watch(() => bankIdLink.value, () => {
    if (logInOnThisDevice.value === false) return;
    if (bankIdLink.value !== null) {
        window.open(bankIdLink.value, '_blank');
    }
});

watch(() => bankIdStatus.value, newVal => {
    loginStore.setBankIdStatus(newVal);
});

onUnmounted(() => {
    void abortBankId();
});
</script>

<template>
    <SText
        is="p"
        v-if="showQrCode"
        size="2"
        class="mb-8"
    >
        {{ logInOnThisDevice === false ? t('components.login.scanQrCode') : t('components.login.bankIdOnThisUnit') }}
    </SText>
    <SStack align-items="center" :vertical="true">
        <img
            src="@/assets/img/bankid.png"
            alt="BankID logo"
            class="mb-5"
            style="width: 6rem;"
        />

        <SAlert
            v-if="errorMessage != null"
            class="mb-10 width-100"
            variant="danger"
            :dismissible="true"
        >
            <div>{{ errorMessage }}</div>
        </SAlert>

        <!-- QR Code -->
        <div v-if="logInOnThisDevice === false" class="w-100">
            <SStack justify-content="center">
                <Transition name="zoom">
                    <img
                        v-if="showQrCode"
                        :src="qrcode"
                        alt="BankID QR Code"
                        class="sy-qr-code p-0 py-0 px-4 display-block mb-7"
                    />
                </Transition>
            </SStack>
        </div>
        <SText v-if="showQrCode" class="mb-10">{{ logInOnThisDevice === false ? $t('components.login.scanAndFollow') : $t('components.login.followInstructions') }}</SText>
        <SText v-if="statusMessage != null" class="mb-10">{{ statusMessage }}</SText>

        <!-- Retry button -->
        <SButton
            v-if="bankIdCanRetry && bankIdInProgress === false"
            variant="primary"
            class="mb-3"
            size="lg"
            block
            @click="loginWithBankId"
        >
            {{ $t('components.login.tryAgain') }}
        </SButton>

        <!-- Cancel -->
        <SButton
            v-if="errorMessage != null || statusMessage != null || showQrCode"
            variant="secondary-light"
            variant-type="clear"
            class="mb-7"
            @click="abortBankId"
        >
            {{ $t('system.cancel') }}
        </SButton>

        <!-- BankID on another device -->
        <SButton
            v-if="!showQrCode && !bankIdCanRetry && !bankIdInProgress"
            variant="primary"
            class="mb-5"
            size="lg"
            block
            @click="startLoginWithBankId(false)"
        >
            {{ $t('components.login.bankIdOtherDevice') }}
        </SButton>

        <!-- BankID on this device -->
        <SButton
            v-if="bankIdInProgress === false && errorMessage == null"
            variant="secondary-light"
            size="lg"
            variant-type="outline"
            block
            @click.prevent="startLoginWithBankId(true)"
        >
            {{ $t('components.login.bankIdThisDevice') }}
        </SButton>
    </SStack>
</template>

<style lang="scss" scoped>
.sy-qr-code {
    max-width: 200px;
}

.z-stack {
    display: grid;

    .z-stack-item {
        grid-row: 1;
        grid-column: 1;
    }
}

.zoom-enter-to,
.zoom-leave-from {
    transition: all 0.5s;
    width: 100%;
}

.zoom-enter-from,
.zoom-leave-to {
    transition: all 0.5s;
    width: 0;
}

.fade-enter-to,
.fade-leave-from {
    transition: all 0.5s;
    opacity: 1;
}

.fade-enter-from,
.fade-leave-to {
    transition: all 0.5s;
    opacity: 0;
}
</style>
